import { type DateTime } from 'luxon';

// How many minutes the time will update when selecting steps in the format
const TIME_STEPS_MINUTES = 5;

export type FormattedDurationOptions = {
  hours?: boolean; // to show hours when applicable
  long?: boolean; // use long suffixes (Stunde/Stunden) instead of short ones (Std.)
  showCa?: boolean; // to add 'ca.' prefix
  showZero?: boolean; // to show '0 Min.' instead of '—' when duration is zero
  steps?: boolean; // to round minutes to nearest TIME_STEPS_MINUTES
};

const addSuffixes = (hours: number, mins: number, options: FormattedDurationOptions): string => {
  const parts: string[] = [];

  let minSuffix = 'Min.';
  let hourSuffix = 'Std.';

  if (options.long) {
    minSuffix = mins === 1 ? 'Minute' : 'Minuten';
    hourSuffix = hours === 1 ? 'Stunde' : 'Stunden';
  }

  if (hours > 0) {
    parts.push(hours.toString());
    parts.push(hourSuffix);
  }

  if (mins > 0) {
    parts.push(Math.round(mins).toString());
    parts.push(minSuffix);
  }

  if (mins === 0 && hours <= 0) {
    parts.push(mins.toString());
    parts.push(minSuffix);
  }

  return parts.join(' ');
};

const buildPrefix = (hours: number, mins: number, options: FormattedDurationOptions): string => {
  if ((mins > 0 || hours > 0) && options.showCa) {
    return 'ca. ';
  }

  return '';
};

export const duration = (
  seconds: number,
  options: FormattedDurationOptions = { hours: true, steps: false },
) => {
  let minutes = Math.round(seconds / 60);
  let hours = 0;

  if (minutes > 60 && options.hours) {
    // This can be a float like 2.85 and we need to get the hours and minutes
    const hoursDecimal = minutes / 60;

    hours = Math.floor(hoursDecimal);

    minutes = 60 * (hoursDecimal - hours);
  }

  if (options.steps) {
    minutes = Math.ceil(minutes / TIME_STEPS_MINUTES) * TIME_STEPS_MINUTES;
  }

  return { hours, minutes };
};

export function roundWithSteps(dt: DateTime): DateTime {
  const minutes = dt.minute;
  const roundedMinutes = Math.round(minutes / TIME_STEPS_MINUTES) * TIME_STEPS_MINUTES;

  return dt.set({ minute: 0, second: 0, millisecond: 0 }).plus({ minutes: roundedMinutes });
}

// Returns a string for the number of seconds provided, in hours and minutes, with a prefix 'ca.'
// if needed. It will use minutes only if in the options you provide { hours: false }.
//
// Example: formattedTimeInMinutes(60) // => 'ca. 1 Min.'
// Example: formattedTimeInMinutes(1200) // => 'ca. 2 Std.'
export const formattedDuration = (
  seconds: number,
  options: FormattedDurationOptions = {
    showCa: true,
    steps: false,
    long: false, // use long suffixes (Stunde/Stunden) instead of short ones (Std.)
    hours: true,
    showZero: false,
  },
) => {
  const durationParts = duration(seconds, options);

  const { hours } = durationParts;
  let { minutes } = durationParts;

  if (minutes === 0 && !options.showZero) {
    return '—';
  }

  if (options.steps) {
    minutes = Math.ceil(minutes / TIME_STEPS_MINUTES) * TIME_STEPS_MINUTES;
  }

  const prefix = buildPrefix(hours, minutes, options);
  const suffixed = addSuffixes(hours, minutes, options);

  return `${prefix}${suffixed}`;
};
